'use client';

import type { Member } from '@slims/shared/types';
import { Button, Stack, Text } from '@slims/ui/atoms';
import { useState } from 'react';
import { toast } from 'sonner';
import { type AdminPick, AdminUserPicker } from '@/components/admin/admin-user-picker';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

export interface AssociateUserBody {
	userId?: string;
	email?: string;
	password?: string;
	fullName?: string;
}

const emptyPick: AdminPick = {
	email: '',
	fullName: '',
	password: '',
	existing: null,
	emailValid: false,
	checking: false,
};

/**
 * Il corpo del collegamento account, senza contenitore.
 *
 * Era il corpo di `MemberUserDialog`, un modale che si apriva da un pulsante
 * nella riga dell'elenco soci. Il modale copriva proprio i dati — email, stato —
 * che servono per decidere quale account collegare; ora il form sta nella tab
 * «Utente» della scheda socio, accanto a quei dati, e il contenitore non serve
 * più. `onDone` resta per chi volesse reincapsularlo altrove.
 */
export function MemberUserForm({
	member,
	onSubmit,
	onDone,
}: {
	member: Member;
	onSubmit: (memberId: string, body: AssociateUserBody) => Promise<Member>;
	/** Chiamata dopo un salvataggio riuscito (il dialog la usa per chiudersi). */
	onDone?: () => void;
}) {
	const orgId = useAdminScopeStore((s) => s.configId);
	const [pick, setPick] = useState<AdminPick>(emptyPick);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	const alreadyLinked = !!member.userId;

	async function handleSubmit() {
		setError('');
		if (!pick.emailValid) {
			setError('Inserisci un indirizzo email valido');
			return;
		}
		if (!pick.existing && pick.password.length < 8) {
			setError('La password deve avere almeno 8 caratteri');
			return;
		}
		setSaving(true);
		try {
			const body: AssociateUserBody = pick.existing
				? { userId: pick.existing.id }
				: { email: pick.email, password: pick.password, fullName: pick.fullName };
			await onSubmit(member.id, body);
			toast.success(
				pick.existing ? 'Account collegato al socio' : 'Account creato e collegato al socio',
			);
			setPick(emptyPick);
			onDone?.();
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : 'Errore nel salvataggio');
		} finally {
			setSaving(false);
		}
	}

	return (
		<Stack gap="sm">
			<Text variant="caption" tone="muted">
				{alreadyLinked
					? 'Questo socio ha già un account collegato. Puoi collegarne uno diverso o crearne uno nuovo.'
					: "Collega un account esistente o creane uno nuovo: il socio potrà accedere al proprio portale."}
			</Text>
			<AdminUserPicker
				lookupUrl={`/api/members/${member.id}/user-lookup?orgId=${orgId}`}
				roleLabel="socio"
				onChange={setPick}
				size="sm"
				initialEmail={member.email ?? ''}
			/>
			{error && (
				<Text variant="micro" tone="danger">
					{error}
				</Text>
			)}
			<Button
				type="button"
				variant="accent"
				size="sm"
				fullWidth
				onClick={handleSubmit}
				disabled={saving || pick.checking || !pick.emailValid}
			>
				{saving
					? 'Salvataggio…'
					: pick.checking
						? 'Verifica…'
						: pick.existing
							? 'Collega account'
							: 'Crea account'}
			</Button>
		</Stack>
	);
}
